import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * Purely presentational state: whether the phone-width slide-out nav drawer is
 * open. The hamburger lives in the header and the drawer lives in the sidenav,
 * and those two are siblings in the app shell, so the open flag has to sit above
 * both of them.
 */
interface NeighborsNavContextValue {
    isMobileNavOpen: boolean;
    closeMobileNav: () => void;
    toggleMobileNav: () => void;
}

const noop = () => undefined;

const NeighborsNavContext = createContext<NeighborsNavContextValue>({
    isMobileNavOpen: false,
    closeMobileNav: noop,
    toggleMobileNav: noop,
});

export const NeighborsNavProvider: React.FC = ({ children }) => {
    const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

    const closeMobileNav = useCallback(() => setIsMobileNavOpen(false), []);
    const toggleMobileNav = useCallback(() => setIsMobileNavOpen((open) => !open), []);

    const value = useMemo(
        () => ({ isMobileNavOpen, closeMobileNav, toggleMobileNav }),
        [isMobileNavOpen, closeMobileNav, toggleMobileNav],
    );

    return <NeighborsNavContext.Provider value={value}>{children}</NeighborsNavContext.Provider>;
};

export const useNeighborsNav = () => useContext(NeighborsNavContext);

export default NeighborsNavContext;
